var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __spreadArray = (this && this.__spreadArray) || function (to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
};
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { t } from 'i18next';
import RadioButton from '../../components/RadioButton';
import '../../styles/widgets/popup/numberChartManipulativePopup.scss';
import { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import NumberCharts from '../NumberCharts';
import { v4 as uuidv4 } from 'uuid';
import Dropdown from '../../components/DropDown';
import InputBox from '../../components/InputBox';
import { DataContext } from '../../player/DataContext';
import globalStore from '../../store';
import CustomScrollbar from '../../components/CustomScrollbar';
import CheckBox from '../../components/CheckBox';
import MathEditor from '../../components/MathEditor';
import FooterActionsButtons from './FooterActionsButtons';
import { DEFAULT_CHART_DATA, CHART_CONFIGS, DISPLAY_VALUES, getDecimalPlaces, } from '../../helper/numberChartHelper';
import { useOnResize } from '../../helper/hooks/onResize';
import { saveState } from '../../helper/stateManagerHelper';
// Moved outside component to prevent re-creation on every render
var DividerLine = function (_a) {
    var className = _a.className;
    return _jsx("div", { className: "seperatorLine ".concat(className !== null && className !== void 0 ? className : '') });
};
// Static constants moved outside component
var RADIO_DIRECTION_OPTS = [
    './assets/images/manipulative/numberChart/topRight.svg',
    './assets/images/manipulative/numberChart/leftDown.svg',
    './assets/images/manipulative/numberChart/bottomRight.svg',
    './assets/images/manipulative/numberChart/bottomUp.svg',
];
var RADIO_DIRECTION_TOOLTIPS = [
    { text: t('text.dirTopRight'), direction: 'topRight' },
    { text: t('text.dirLeftDown'), direction: 'top' },
    { text: t('text.dirBottomRight'), direction: 'top' },
    { text: t('text.dirBottomUp'), direction: 'topLeft' },
];
// GCD function extracted to avoid duplication
var gcd = function (a, b) {
    if (!Number.isFinite(a) || !Number.isFinite(b))
        return 1;
    if (a === 0 && b === 0)
        return 1;
    return b === 0 ? Math.abs(a) : gcd(b, a % b);
};
// Helper function to convert decimal to fraction
var decimalToFraction = function (decimal) {
    if (decimal === 0)
        return { numerator: 0, denominator: 1 };
    var sign = decimal < 0 ? -1 : 1;
    decimal = Math.abs(decimal);
    // Handle the decimal part
    var tolerance = 1.0e-6;
    var numerator = 1;
    var denominator = 1;
    var h1 = 1, h2 = 0, k1 = 0, k2 = 1;
    var b = decimal;
    do {
        var a = Math.floor(b);
        var aux = h1;
        h1 = a * h1 + h2;
        h2 = aux;
        aux = k1;
        k1 = a * k1 + k2;
        k2 = aux;
        b = 1 / (b - a);
    } while (Math.abs(decimal - h1 / k1) > decimal * tolerance && k1 < 10000);
    numerator = h1 * sign;
    denominator = k1;
    return { numerator: numerator, denominator: denominator };
};
// Helper function to convert fraction to mixed number
var fractionToMixed = function (numerator, denominator) {
    if (denominator === 0)
        return { integer: 0, numerator: 0, denominator: 1 };
    var integer = Math.floor(Math.abs(numerator) / denominator) * (numerator < 0 ? -1 : 1);
    var remainder = Math.abs(numerator % denominator);
    return { integer: integer, numerator: remainder, denominator: denominator };
};
// Helper function to convert mixed number to improper fraction
var mixedToFraction = function (integer, numerator, denominator) {
    var improperNumerator = integer * denominator + numerator;
    return { numerator: improperNumerator, denominator: denominator };
};
// Helper function to convert fraction to decimal
var fractionToDecimal = function (numerator, denominator) {
    if (denominator === 0)
        return 0;
    return numerator / denominator;
};
// Base cell structure to avoid duplication
var BASE_CELL = {
    visible: true,
    locked: false,
    iconIndex: [],
    colorIndex: [],
    dragIndex: -1,
    opacity: 1,
    placedValue: '',
    highlightColor: null,
};
// Inline styles extracted to constant
var flexCenterStyle = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '10px',
};
// Convert MathJax format \\(...\\) to MathEditor format
var convertToMathEditorFormat = function (value) {
    if (!value)
        return [{ type: 'text', value: '' }];
    // Strip \\( and \\) wrappers and return as math array
    var latex = value.replace(/^\\\\\(/, '').replace(/\\\\\)$/, '');
    return [{ type: 'math', latex: latex }];
};
// Validation regex patterns
var VALID_DIGITS_REGEX = /^\d+$/;
var VALID_DECIMAL_REGEX = /^\d+(\.\d{0,5})?$/;
var MAXLENGTH = 6;
// Validation helper for integer inputs
var shouldBlockIntegerInput = function (numberType, isInteger, isValidDecimal) {
    if ((numberType === 0 || numberType === 2) && !isInteger) {
        return true;
    }
    if (numberType === 3 && !isValidDecimal) {
        return true;
    }
    return false;
};
// Validation helper for fraction inputs
var shouldBlockFractionInput = function (numberType, isInteger) {
    return (numberType === 1 || numberType === 2) && !isInteger;
};
// Input field key mapping
var INPUT_FIELD_KEYS = {
    'start-value-integer': 'startInteger',
    'start-value-numerator': 'startNumerator',
    'start-value-denominator': 'startDenominator',
    'change-value-integer': 'changeInteger',
    'change-value-numerator': 'changeNumerator',
    'change-value-denominator': 'changeDenominator',
};
var NumberChartManipulativePopup = function (props) {
    var _a;
    var _b = props.data, data = _b === void 0 ? DEFAULT_CHART_DATA : _b;
    // Memoized translation arrays
    var ADJUSTMENT_OPTS = useMemo(function () { return [t('text.incBy'), t('text.decBy')]; }, []);
    var RADIO_OPTS = useMemo(function () { return [
        t('text.numberChart1'),
        t('text.numberChart2'),
        t('text.numberChart3'),
        t('text.numberChart4'),
        t('text.numberChart5'),
    ]; }, []);
    var NUMBER_TYPE_OPTS = useMemo(function () { return [t('text.integers'), t('text.fractions'), t('text.mixedNumbers'), t('text.decimals')]; }, []);
    var _c = useContext(DataContext), setSelectedManipulativeId = _c.setSelectedManipulativeId, setManiplulatorComponentVisible = _c.setManiplulatorComponentVisible, setActiveManipulator = _c.setActiveManipulator, selectedManipulativeId = _c.selectedManipulativeId, setManipulative = _c.setManipulative, manipulative = _c.manipulative, getPositionForNewItem = _c.getPositionForNewItem, setSelectedTool = _c.setSelectedTool;
    var _d = useState(data), numberChartData = _d[0], setNumberChartData = _d[1];
    var _e = useState(1), previewScale = _e[0], setPreviewScale = _e[1];
    var zoomLevel = globalStore.useStoreState(function (state) { return state.playerStore; }).zoomLevel;
    var _f = useState({
        row: 1,
        col: 1,
        startInteger: '1',
        startNumerator: 1,
        startDenominator: 1,
        changeInteger: '1',
        changeNumerator: 1,
        changeDenominator: 1,
    }), inputValObj = _f[0], setInputValObj = _f[1];
    var _g = useState(false), rowError = _g[0], setRowError = _g[1];
    var _h = useState(''), popupAnnounceText = _h[0], setPopupAnnounceText = _h[1];
    var _j = useState(false), colError = _j[0], setColError = _j[1];
    var _k = useState(false), startIntError = _k[0], setStartIntError = _k[1];
    var _l = useState(false), changeIntError = _l[0], setChangeIntError = _l[1];
    var _m = useState(false), startNumError = _m[0], setStartNumError = _m[1];
    var _o = useState(false), startDenError = _o[0], setStartDenError = _o[1];
    var _p = useState(false), changeNumError = _p[0], setChangeNumError = _p[1];
    var _q = useState(false), changeDenError = _q[0], setChangeDenError = _q[1];
    var _r = useState(false), startDenZeroError = _r[0], setStartDenZeroError = _r[1];
    var _s = useState(false), changeDenZeroError = _s[0], setChangeDenZeroError = _s[1];
    var _t = useState(false), startDecimalPrecisionError = _t[0], setStartDecimalPrecisionError = _t[1];
    var _u = useState(false), changeDecimalPrecisionError = _u[0], setChangeDecimalPrecisionError = _u[1];
    // Memoized isRowColInvalid to avoid recalculation
    var isRowColInvalid = useMemo(function () {
        return numberChartData.chartType === 4 &&
            (inputValObj.row < 1 || inputValObj.row > 20 || inputValObj.col < 1 || inputValObj.col > 20);
    }, [numberChartData.chartType, inputValObj.row, inputValObj.col]);
    var chartDisplayRef = useRef(null);
    // Memoized isDoneDisable
    var isDoneDisabled = useMemo(function () {
        var startInteger = inputValObj.startInteger, changeInteger = inputValObj.changeInteger, startNumerator = inputValObj.startNumerator, startDenominator = inputValObj.startDenominator, changeNumerator = inputValObj.changeNumerator, changeDenominator = inputValObj.changeDenominator;
        if (numberChartData.numberType === 0) {
            var startStr = String(startInteger);
            var changeStr = String(changeInteger);
            // Disable if fields are empty (cleared after validation error)
            if (startStr === '' || changeStr === '') {
                return true;
            }
            var hasInvalidChars = !VALID_DIGITS_REGEX.test(startStr) || !VALID_DIGITS_REGEX.test(changeStr);
            if (startStr.length > MAXLENGTH || changeStr.length > MAXLENGTH || hasInvalidChars) {
                return true;
            }
            // Disable if change value is 0
            if (Number(changeInteger) === 0) {
                return true;
            }
        }
        if (numberChartData.numberType === 1) {
            var startNumStr = String(startNumerator);
            var startDenStr = String(startDenominator);
            var changeNumStr = String(changeNumerator);
            var changeDenStr = String(changeDenominator);
            var hasInvalidChars = !VALID_DIGITS_REGEX.test(startNumStr) ||
                !VALID_DIGITS_REGEX.test(startDenStr) ||
                !VALID_DIGITS_REGEX.test(changeNumStr) ||
                !VALID_DIGITS_REGEX.test(changeDenStr);
            var exceedsLength = startNumStr.length > MAXLENGTH ||
                startDenStr.length > MAXLENGTH ||
                changeNumStr.length > MAXLENGTH ||
                changeDenStr.length > MAXLENGTH;
            if (hasInvalidChars || exceedsLength) {
                return true;
            }
            // Disable if denominator or change values are 0
            if (Number(startDenominator) === 0 ||
                Number(changeNumerator) === 0 ||
                Number(changeDenominator) === 0) {
                return true;
            }
        }
        if (numberChartData.numberType === 2) {
            var startIntStr = String(startInteger);
            var startNumStr = String(startNumerator);
            var startDenStr = String(startDenominator);
            var changeIntStr = String(changeInteger);
            var changeNumStr = String(changeNumerator);
            var changeDenStr = String(changeDenominator);
            var hasInvalidChars = !VALID_DIGITS_REGEX.test(startIntStr) ||
                !VALID_DIGITS_REGEX.test(startNumStr) ||
                !VALID_DIGITS_REGEX.test(startDenStr) ||
                !VALID_DIGITS_REGEX.test(changeIntStr) ||
                !VALID_DIGITS_REGEX.test(changeNumStr) ||
                !VALID_DIGITS_REGEX.test(changeDenStr);
            var exceedsLength = startIntStr.length > MAXLENGTH ||
                startNumStr.length > MAXLENGTH ||
                startDenStr.length > MAXLENGTH ||
                changeIntStr.length > MAXLENGTH ||
                changeNumStr.length > MAXLENGTH ||
                changeDenStr.length > MAXLENGTH;
            if (hasInvalidChars || exceedsLength) {
                return true;
            }
            // Disable if denominators are 0
            if (Number(startDenominator) === 0 || Number(changeDenominator) === 0) {
                return true;
            }
            // For mixed numbers, total change value must be non-zero
            // (integer * denominator + numerator must be > 0)
            var totalChangeValue = Number(changeInteger) * Number(changeDenominator) + Number(changeNumerator);
            if (totalChangeValue === 0) {
                return true;
            }
        }
        if (numberChartData.numberType === 3) {
            var startStr = String(startInteger);
            var changeStr = String(changeInteger);
            var hasInvalidChars = !VALID_DECIMAL_REGEX.test(startStr) || !VALID_DECIMAL_REGEX.test(changeStr);
            var startIntPart = startStr.split('.')[0];
            var changeIntPart = changeStr.split('.')[0];
            var exceedsLength = startIntPart.length > MAXLENGTH || changeIntPart.length > MAXLENGTH;
            if (hasInvalidChars || exceedsLength) {
                return true;
            }
            // Disable if change value is 0
            if (Number(changeInteger) === 0) {
                return true;
            }
        }
        return isRowColInvalid;
    }, [numberChartData.numberType, inputValObj, isRowColInvalid]);
    // Helper function to calculate fraction numerator
    // For mixed numbers, pass improper fractions (integer * denominator + numerator)
    var calculateFractionNumerator = useCallback(function (index, startNumerator, startDenominator, countNumerator, countDenominator, countDirection) {
        // Formula for adding/subtracting fractions:
        // (a/b) ± index * (c/d) = (a*d ± index*c*b) / (b*d)
        // This function returns the numerator: a*d ± index*c*b
        if (countDirection === 0) {
            return startNumerator * countDenominator + index * countNumerator * startDenominator;
        }
        else {
            return startNumerator * countDenominator - index * countNumerator * startDenominator;
        }
    }, []);
    var createReqData = useCallback(function (_a) {
        var _b = _a.chartType, chartType = _b === void 0 ? numberChartData.chartType : _b, _c = _a.numberType, numberType = _c === void 0 ? numberChartData.numberType : _c, _d = _a.startNumerator, startNumerator = _d === void 0 ? numberChartData.startNumerator : _d, _e = _a.startDenominator, startDenominator = _e === void 0 ? numberChartData.startDenominator : _e, _f = _a.countNumerator, countNumerator = _f === void 0 ? numberChartData.countNumerator : _f, _g = _a.countDenominator, countDenominator = _g === void 0 ? numberChartData.countDenominator : _g, _h = _a.countDirection, countDirection = _h === void 0 ? numberChartData.countDirection : _h, _j = _a.rows, rows = _j === void 0 ? numberChartData.rows : _j, _k = _a.columns, columns = _k === void 0 ? numberChartData.columns : _k;
        var maxDecimalPlaces = Math.max(getDecimalPlaces(startNumerator), getDecimalPlaces(countNumerator));
        var calculateCellValue = function (index) {
            // For integers (numberType === 0), use simple arithmetic
            if (numberType === 0) {
                if (countDirection === 0) {
                    return startNumerator + index * countNumerator;
                }
                else {
                    return startNumerator - index * countNumerator;
                }
            }
            // For fractions (numberType === 1), mixed numbers (numberType === 2), and decimals (numberType === 3)
            // use division-based arithmetic
            var startValue = startNumerator / startDenominator;
            var countValue = countNumerator / countDenominator;
            if (countDirection === 0) {
                return startValue + index * countValue;
            }
            else {
                return startValue - index * countValue;
            }
        };
        var createCellWithValue = function (index) {
            var calculatedValue = calculateCellValue(index);
            if (numberType === 0) {
                return __assign(__assign({}, BASE_CELL), { numerator: Number(calculatedValue), denominator: 1, value: "".concat(calculatedValue) });
            }
            else if (numberType === 2) {
                if (!startDenominator || !countDenominator)
                    return __assign(__assign({}, BASE_CELL), { numerator: 0, denominator: 1, value: '' });
                // Use least common denominator (LCD) via LCM
                var denomGcd = gcd(startDenominator, countDenominator);
                var commonDenom = (startDenominator * countDenominator) / denomGcd;
                var rawNumerator = calculateFractionNumerator(index, startNumerator, startDenominator, countNumerator, countDenominator, countDirection);
                // Adjust numerator for LCD (divide by GCD)
                var numerator = rawNumerator / denomGcd;
                var finalNumerator = numerator;
                var finalDenominator = commonDenom;
                if (numberChartData.simplfyFractions) {
                    var divisor = gcd(Math.abs(numerator), commonDenom);
                    finalNumerator = numerator / divisor;
                    finalDenominator = commonDenom / divisor;
                }
                var integerPart = Math.trunc(finalNumerator / finalDenominator);
                var remainderNumerator = Math.abs(finalNumerator % finalDenominator);
                var displayValue = void 0;
                if (remainderNumerator === 0) {
                    displayValue = "\\(".concat(integerPart, "\\)");
                }
                else if (integerPart === 0) {
                    var sign = finalNumerator < 0 ? '-' : '';
                    displayValue = "\\(".concat(sign, "\\frac{").concat(Math.abs(finalNumerator), "}{").concat(finalDenominator, "}\\)");
                }
                else {
                    var sign = finalNumerator < 0 ? '-' : '';
                    displayValue = "\\(".concat(sign).concat(Math.abs(integerPart), "\\frac{").concat(remainderNumerator, "}{").concat(finalDenominator, "}\\)");
                }
                return __assign(__assign({}, BASE_CELL), { numerator: finalNumerator, denominator: finalDenominator, value: displayValue });
            }
            else if (numberType === 1) {
                if (!startDenominator || !countDenominator)
                    return __assign(__assign({}, BASE_CELL), { numerator: 0, denominator: 1, value: '' });
                var denomGcd = gcd(startDenominator, countDenominator);
                var commonDenom = (startDenominator * countDenominator) / denomGcd;
                var rawNumerator = calculateFractionNumerator(index, startNumerator, startDenominator, countNumerator, countDenominator, countDirection);
                var numerator = rawNumerator / denomGcd;
                var finalNumerator = numerator;
                var finalDenominator = commonDenom;
                if (numberChartData.simplfyFractions) {
                    var divisor = gcd(Math.abs(numerator), commonDenom);
                    finalNumerator = numerator / divisor;
                    finalDenominator = commonDenom / divisor;
                }
                // When simplified to whole number (denominator is 1), display as integer
                if (finalDenominator === 1) {
                    return __assign(__assign({}, BASE_CELL), { numerator: finalNumerator, denominator: 1, value: "\\(".concat(finalNumerator, "\\)") });
                }
                var sign = finalNumerator < 0 ? '-' : '';
                return __assign(__assign({}, BASE_CELL), { numerator: finalNumerator, denominator: finalDenominator, value: "\\(".concat(sign, "\\frac{").concat(Math.abs(finalNumerator), "}{").concat(finalDenominator, "}\\)") });
            }
            else {
                // For decimal type (numberType === 3), trim trailing zeros (e.g. "1.50" → "1.5", "2.00" → "2")
                var value = parseFloat(calculatedValue.toFixed(2)).toString();
                return __assign(__assign({}, BASE_CELL), { numerator: Number(value), denominator: 1, value: "\\(".concat(value, "\\)") });
            }
        };
        // Generate cells using for loops for better performance
        var cells = [];
        var cellIndex = 0;
        for (var r = 0; r < rows; r++) {
            var row = [];
            for (var c = 0; c < columns; c++) {
                row.push(createCellWithValue(cellIndex));
                cellIndex++;
            }
            cells.push(row);
        }
        return __assign(__assign({}, numberChartData), { chartType: chartType, numberType: numberType, startNumerator: startNumerator, startDenominator: startDenominator, countNumerator: countNumerator, countDenominator: countDenominator, countDirection: countDirection, rows: rows, columns: columns, cells: cells });
    }, [numberChartData, calculateFractionNumerator]);
    // Memoized onRadioChange
    var onRadioChange = useCallback(function (value) {
        var config = CHART_CONFIGS[value];
        if (!config)
            return;
        // Update inputValObj with the new config values for rows/columns
        setInputValObj(function (prev) { return (__assign(__assign({}, prev), { row: config.rows, col: config.columns, startInteger: String(config.startNumerator), startNumerator: config.startNumerator, startDenominator: 1, changeInteger: String(config.countNumerator), changeNumerator: config.countNumerator, changeDenominator: 1 })); });
        var _dta = createReqData({
            chartType: value,
            numberType: 0,
            rows: config.rows,
            columns: config.columns,
            countDirection: 0,
            startNumerator: config.startNumerator,
            startDenominator: 1,
            countDenominator: 1,
            countNumerator: config.countNumerator,
        });
        setNumberChartData(function (prev) { return (__assign(__assign({}, prev), _dta)); });
        setPopupAnnounceText('');
        requestAnimationFrame(function () {
            setPopupAnnounceText(t('text.gridSizeChangedAnnounce', { rows: config.rows, columns: config.columns }));
        });
    }, [createReqData]);
    // Initial effect with proper dependencies
    useEffect(function () {
        if ((data === null || data === void 0 ? void 0 : data.chartType) === 0)
            onRadioChange(0);
        // Initialize input fields from existing data
        // For mixed numbers (numberType === 2), convert improper fraction to mixed number format
        // Check if this is a new mixed number chart with default values
        var isNewMixedNumber = data.numberType === 2 &&
            data.startNumerator === 1 &&
            data.startDenominator === 1 &&
            data.countNumerator === 1 &&
            data.countDenominator === 1;
        var startInt, startNum, startDen, changeInt, changeNum, changeDen;
        if (data.numberType === 2) {
            if (isNewMixedNumber) {
                // For new mixed number charts, set custom defaults: Start = 0 0/1, Change = 0 1/1
                startInt = '0';
                startNum = 0;
                startDen = 1;
                changeInt = '0';
                changeNum = 1;
                changeDen = 1;
            }
            else {
                // For existing mixed number charts, convert improper fraction to mixed number
                var startIntPart = Math.floor(data.startNumerator / data.startDenominator);
                var startRemainderNum = data.startNumerator % data.startDenominator;
                startInt = String(startIntPart);
                startNum = startRemainderNum;
                startDen = data.startDenominator;
                var changeIntPart = Math.floor(data.countNumerator / data.countDenominator);
                var changeRemainderNum = data.countNumerator % data.countDenominator;
                changeInt = String(changeIntPart);
                changeNum = changeRemainderNum;
                changeDen = data.countDenominator;
            }
        }
        else {
            // For other number types, use data values directly
            startInt = String(data.startNumerator || 1);
            startNum = data.startNumerator || 1;
            startDen = data.startDenominator || 1;
            changeInt = String(data.countNumerator || 1);
            changeNum = data.countNumerator || 1;
            changeDen = data.countDenominator || 1;
        }
        setInputValObj(function (prev) { return (__assign(__assign({}, prev), { row: data.rows, col: data.columns, startInteger: startInt, startNumerator: startNum, startDenominator: startDen, changeInteger: changeInt, changeNumerator: changeNum, changeDenominator: changeDen })); });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    // Regenerate cells when simplfyFractions changes
    useEffect(function () {
        if ((numberChartData === null || numberChartData === void 0 ? void 0 : numberChartData.chartType) === 4 &&
            (numberChartData === null || numberChartData === void 0 ? void 0 : numberChartData.rows) > 0 &&
            (numberChartData === null || numberChartData === void 0 ? void 0 : numberChartData.columns) > 0) {
            var _dta_1 = createReqData({});
            setNumberChartData(function (prev) { return (__assign(__assign({}, prev), _dta_1)); });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [numberChartData === null || numberChartData === void 0 ? void 0 : numberChartData.simplfyFractions]);
    var onBtnClick = useCallback(function (type) {
        var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k;
        if (type === 'done') {
            if (isRowColInvalid || isDoneDisabled) {
                return;
            }
            var updatedData = createReqData({});
            var _existingChart_1 = selectedManipulativeId !== ''
                ? manipulative.find(function (m) { return m.data.id === selectedManipulativeId && m.data.type === 'numberCharts'; })
                : undefined;
            var _toBeUpdated_1;
            if (_existingChart_1) {
                _toBeUpdated_1 = __assign(__assign({}, _existingChart_1), { data: __assign(__assign(__assign({}, _existingChart_1.data), updatedData), { type: 'numberCharts', lock: {
                            positioning: (_b = (_a = _existingChart_1.data.lock) === null || _a === void 0 ? void 0 : _a.positioning) !== null && _b !== void 0 ? _b : false,
                            editing: (_d = (_c = _existingChart_1.data.lock) === null || _c === void 0 ? void 0 : _c.editing) !== null && _d !== void 0 ? _d : false,
                        } }) });
                saveState({
                    type: selectedManipulativeId,
                    id: ((_f = (_e = _toBeUpdated_1 === null || _toBeUpdated_1 === void 0 ? void 0 : _toBeUpdated_1.data) === null || _e === void 0 ? void 0 : _e.id) !== null && _f !== void 0 ? _f : '').split('_')[1],
                    propertyName: 'completeData',
                    old: JSON.stringify(_existingChart_1.data),
                    new: JSON.stringify(_toBeUpdated_1.data),
                });
            }
            else {
                var _l = getPositionForNewItem(), x = _l.x, y = _l.y;
                var newNumberChartId = "numberChart_".concat(uuidv4().replace(/-/g, ''));
                _toBeUpdated_1 = {
                    type: 'numberCharts',
                    widgetId: "numberChart:".concat(uuidv4().replace(/-/g, '')),
                    data: __assign(__assign({}, updatedData), { center: { x: x, y: y }, id: newNumberChartId, type: 'numberCharts', lock: { positioning: false, editing: false } }),
                };
                saveState({
                    type: (_g = _toBeUpdated_1.data.id) !== null && _g !== void 0 ? _g : '',
                    id: ((_j = (_h = _toBeUpdated_1 === null || _toBeUpdated_1 === void 0 ? void 0 : _toBeUpdated_1.data) === null || _h === void 0 ? void 0 : _h.id) !== null && _j !== void 0 ? _j : '').split('_')[1],
                    propertyName: 'create',
                    old: JSON.stringify(_toBeUpdated_1),
                });
            }
            setManipulative(function (prev) {
                if (_existingChart_1) {
                    return prev.map(function (m) { return (m.data.id === selectedManipulativeId ? _toBeUpdated_1 : m); });
                }
                else {
                    return __spreadArray(__spreadArray([], prev, true), [_toBeUpdated_1], false);
                }
            });
            // Auto-select and focus the number chart after popup closes
            var focusId_1 = _existingChart_1
                ? selectedManipulativeId
                : (_k = _toBeUpdated_1.data.id) !== null && _k !== void 0 ? _k : '';
            setSelectedTool([focusId_1]);
            setTimeout(function () {
                var _a;
                var wrapper = document.querySelector("[data-manipulative-id=\"".concat(focusId_1, "\"]"));
                var gridBtn = wrapper === null || wrapper === void 0 ? void 0 : wrapper.querySelector('.gridParent');
                (_a = (gridBtn !== null && gridBtn !== void 0 ? gridBtn : wrapper)) === null || _a === void 0 ? void 0 : _a.focus();
            }, 200);
        }
        setSelectedManipulativeId('');
        setManiplulatorComponentVisible(false);
        setActiveManipulator('');
    }, [
        isRowColInvalid,
        isDoneDisabled,
        createReqData,
        numberChartData.id,
        selectedManipulativeId,
        setManipulative,
        setSelectedManipulativeId,
        setManiplulatorComponentVisible,
        setActiveManipulator,
    ]);
    var onUpdateRowCol = useCallback(function (id, value, _event) {
        var isRow = id === 'row-selector';
        // Clear error when user starts typing
        if (isRow)
            setRowError(false);
        else
            setColError(false);
        // Allow empty input (user is still typing)
        if (value === '') {
            setInputValObj(function (prev) { return (__assign(__assign({}, prev), (isRow ? { row: 0 } : { col: 0 }))); });
            return;
        }
        var numValue = Number(value);
        var isInvalid = !Number.isInteger(numValue) || numValue < 1 || numValue > 20;
        if (isInvalid) {
            // Clear the field and show error
            setInputValObj(function (prev) { return (__assign(__assign({}, prev), (isRow ? { row: 0 } : { col: 0 }))); });
            if (isRow)
                setRowError(true);
            else
                setColError(true);
            return;
        }
        // Valid: update state and regenerate chart
        setInputValObj(function (prev) { return (__assign(__assign({}, prev), (isRow ? { row: numValue } : { col: numValue }))); });
        var _dta = createReqData(isRow ? { rows: numValue } : { columns: numValue });
        setNumberChartData(function (prev) { return (__assign(__assign({}, prev), _dta)); });
    }, [createReqData]);
    // ==================== TYPE-SPECIFIC INPUT HANDLERS ====================
    // Handler for Integer type (numberType === 0)
    var handleIntegerInput = useCallback(function (id, value, numValue) {
        var _a;
        var isStart = id === 'start-value-integer';
        var inputKey = INPUT_FIELD_KEYS[id];
        // Clear error when user starts typing
        if (isStart)
            setStartIntError(false);
        else
            setChangeIntError(false);
        // Update input state
        setInputValObj(function (prev) {
            var _a;
            return (__assign(__assign({}, prev), (_a = {}, _a[inputKey] = value, _a)));
        });
        // Validate integer and range
        var isInteger = Number.isInteger(numValue);
        // Start value: allow 0 to 100000
        // Change value: allow 1 to 100000 (can't count by 0)
        var minValue = isStart ? 0 : 1;
        if (numValue < minValue || numValue > 100000 || !isInteger) {
            setInputValObj(function (prev) {
                var _a;
                return (__assign(__assign({}, prev), (_a = {}, _a[inputKey] = '', _a)));
            });
            if (isStart)
                setStartIntError(true);
            else
                setChangeIntError(true);
            return;
        }
        // Update chart data
        var reqDataKey = isStart ? 'startNumerator' : 'countNumerator';
        var _dta = createReqData((_a = {},
            _a[reqDataKey] = numValue,
            _a[isStart ? 'startDenominator' : 'countDenominator'] = 1,
            _a));
        setNumberChartData(function (prev) { return (__assign(__assign({}, prev), _dta)); });
    }, [createReqData]);
    // Handler for Decimal type (numberType === 3)
    var handleDecimalInput = useCallback(function (id, value, numValue) {
        var _a;
        var isStart = id === 'start-value-integer';
        var inputKey = INPUT_FIELD_KEYS[id];
        // Clear error when user starts typing (before validation checks)
        if (isStart) {
            setStartIntError(false);
            setStartDecimalPrecisionError(false);
        }
        else {
            setChangeIntError(false);
            setChangeDecimalPrecisionError(false);
        }
        // Normalize leading dot: ".9" → "0.9"
        if (value.startsWith('.')) {
            value = '0' + value;
        }
        // Block invalid decimal format (allows up to 5 decimal places)
        // Allow empty string for clearing, or valid decimal format
        if (value !== '' && !VALID_DECIMAL_REGEX.test(value)) {
            return;
        }
        // Block more than 2 decimal places on change — show warning immediately
        var decimalMatch = value.match(/\.(\d+)/);
        if (decimalMatch && decimalMatch[1].length > 2) {
            setInputValObj(function (prev) {
                var _a;
                return (__assign(__assign({}, prev), (_a = {}, _a[inputKey] = '', _a)));
            });
            if (isStart)
                setStartDecimalPrecisionError(true);
            else
                setChangeDecimalPrecisionError(true);
            return;
        }
        // Update input state
        setInputValObj(function (prev) {
            var _a;
            return (__assign(__assign({}, prev), (_a = {}, _a[inputKey] = value, _a)));
        });
        // Validate range
        // Start value: allow 0 to 100000
        // Change value: allow 0.01 to 100000
        var minValue = isStart ? 0 : 0.01;
        if (numValue < minValue || numValue > 100000) {
            setInputValObj(function (prev) {
                var _a;
                return (__assign(__assign({}, prev), (_a = {}, _a[inputKey] = '', _a)));
            });
            if (isStart)
                setStartIntError(true);
            else
                setChangeIntError(true);
            return;
        }
        // Update chart data
        var reqDataKey = isStart ? 'startNumerator' : 'countNumerator';
        var _dta = createReqData((_a = {},
            _a[reqDataKey] = numValue,
            _a[isStart ? 'startDenominator' : 'countDenominator'] = 1,
            _a));
        setNumberChartData(function (prev) { return (__assign(__assign({}, prev), _dta)); });
    }, [createReqData]);
    // Handler for Fraction type (numberType === 1)
    var handleFractionInput = useCallback(function (id, value, numValue) {
        var _a, _b;
        var inputKey = INPUT_FIELD_KEYS[id];
        var isStart = id.includes('start-value');
        var isNumerator = id.includes('numerator');
        // Clear error for the specific input when user starts typing
        if (isStart) {
            if (isNumerator)
                setStartNumError(false);
            else {
                setStartDenError(false);
                setStartDenZeroError(false);
            }
        }
        else {
            if (isNumerator)
                setChangeNumError(false);
            else {
                setChangeDenError(false);
                setChangeDenZeroError(false);
            }
        }
        // Update input state
        setInputValObj(function (prev) {
            var _a;
            return (__assign(__assign({}, prev), (_a = {}, _a[inputKey] = value, _a)));
        });
        // Block non-integer input
        var isInteger = Number.isInteger(numValue);
        if (!isInteger)
            return;
        // Validate range (same as integer validation)
        if (numValue > 100000 || numValue < 0) {
            setInputValObj(function (prev) {
                var _a;
                return (__assign(__assign({}, prev), (_a = {}, _a[inputKey] = '', _a)));
            });
            if (isStart) {
                if (isNumerator)
                    setStartNumError(true);
                else
                    setStartDenError(true);
            }
            else {
                if (isNumerator)
                    setChangeNumError(true);
                else
                    setChangeDenError(true);
            }
            return;
        }
        if (isNumerator) {
            // Update numerator
            var reqDataKey = isStart ? 'startNumerator' : 'countNumerator';
            var _dta_2 = createReqData((_a = {}, _a[reqDataKey] = numValue, _a));
            setNumberChartData(function (prev) { return (__assign(__assign({}, prev), _dta_2)); });
        }
        else {
            // Denominator cannot be zero
            if (numValue === 0) {
                if (isStart)
                    setStartDenZeroError(true);
                else
                    setChangeDenZeroError(true);
                return;
            }
            // Update denominator
            var reqDataKey = isStart ? 'startDenominator' : 'countDenominator';
            var _dta_3 = createReqData((_b = {}, _b[reqDataKey] = numValue, _b));
            setNumberChartData(function (prev) { return (__assign(__assign({}, prev), _dta_3)); });
        }
    }, [createReqData]);
    // Handler for Mixed Number type (numberType === 2)
    var handleMixedNumberInput = useCallback(function (id, value, numValue) {
        var _a, _b, _c;
        var inputKey = INPUT_FIELD_KEYS[id];
        var isStart = id.includes('start-value');
        // Handle integer part
        if (id === 'start-value-integer' || id === 'change-value-integer') {
            // Clear error when user starts typing
            if (isStart)
                setStartIntError(false);
            else
                setChangeIntError(false);
            // Update input state
            setInputValObj(function (prev) {
                var _a;
                return (__assign(__assign({}, prev), (_a = {}, _a[inputKey] = value, _a)));
            });
            // Validate integer type
            var isInteger = Number.isInteger(numValue);
            // Validate range - for mixed numbers, both start and change can be 0
            // (total change value validation is in isDoneDisabled)
            var minValue = 0;
            if (numValue < minValue || numValue > 100000 || !isInteger) {
                setInputValObj(function (prev) {
                    var _a;
                    return (__assign(__assign({}, prev), (_a = {}, _a[inputKey] = '', _a)));
                }); // Clear field
                if (isStart)
                    setStartIntError(true); // Set error
                else
                    setChangeIntError(true);
                return;
            }
            // Get current fraction values
            var numerator = isStart
                ? Number(inputValObj.startNumerator)
                : Number(inputValObj.changeNumerator);
            var denominator = isStart
                ? Number(inputValObj.startDenominator)
                : Number(inputValObj.changeDenominator);
            // Convert to improper fraction
            var improperNumerator = numValue * denominator + numerator;
            var reqDataKey = isStart ? 'startNumerator' : 'countNumerator';
            var reqDataDenomKey = isStart ? 'startDenominator' : 'countDenominator';
            var _dta_4 = createReqData((_a = {},
                _a[reqDataKey] = improperNumerator,
                _a[reqDataDenomKey] = denominator,
                _a));
            setNumberChartData(function (prev) { return (__assign(__assign({}, prev), _dta_4)); });
        }
        // Handle numerator
        else if (id === 'start-value-numerator' || id === 'change-value-numerator') {
            // Clear error when user starts typing
            if (isStart)
                setStartNumError(false);
            else
                setChangeNumError(false);
            // Block non-integer
            var isInteger = Number.isInteger(numValue);
            if (!isInteger)
                return;
            // Update input state
            setInputValObj(function (prev) {
                var _a;
                return (__assign(__assign({}, prev), (_a = {}, _a[inputKey] = value, _a)));
            });
            // Validate range
            if (numValue > 100000 || numValue < 0) {
                setInputValObj(function (prev) {
                    var _a;
                    return (__assign(__assign({}, prev), (_a = {}, _a[inputKey] = '', _a)));
                });
                if (isStart)
                    setStartNumError(true);
                else
                    setChangeNumError(true);
                return;
            }
            var integer = isStart
                ? Number(inputValObj.startInteger)
                : Number(inputValObj.changeInteger);
            var numerator = numValue;
            var denominator = isStart
                ? Number(inputValObj.startDenominator)
                : Number(inputValObj.changeDenominator);
            // Convert to improper fraction (no auto-normalization)
            var improperNumerator = integer * denominator + numerator;
            var reqDataKey = isStart ? 'startNumerator' : 'countNumerator';
            var reqDataDenomKey = isStart ? 'startDenominator' : 'countDenominator';
            var _dta_5 = createReqData((_b = {},
                _b[reqDataKey] = improperNumerator,
                _b[reqDataDenomKey] = denominator,
                _b));
            setNumberChartData(function (prev) { return (__assign(__assign({}, prev), _dta_5)); });
        }
        // Handle denominator
        else if (id === 'start-value-denominator' || id === 'change-value-denominator') {
            // Clear error when user starts typing
            if (isStart) {
                setStartDenError(false);
                setStartDenZeroError(false);
            }
            else {
                setChangeDenError(false);
                setChangeDenZeroError(false);
            }
            // Block non-integer
            var isInteger = Number.isInteger(numValue);
            if (!isInteger)
                return;
            // Update input state
            setInputValObj(function (prev) {
                var _a;
                return (__assign(__assign({}, prev), (_a = {}, _a[inputKey] = value, _a)));
            });
            // Validate range
            if (numValue > 100000 || numValue < 0) {
                setInputValObj(function (prev) {
                    var _a;
                    return (__assign(__assign({}, prev), (_a = {}, _a[inputKey] = '', _a)));
                });
                if (isStart)
                    setStartDenError(true);
                else
                    setChangeDenError(true);
                return;
            }
            // Denominator cannot be zero
            if (numValue === 0) {
                if (isStart)
                    setStartDenZeroError(true);
                else
                    setChangeDenZeroError(true);
                return;
            }
            var integer = isStart
                ? Number(inputValObj.startInteger)
                : Number(inputValObj.changeInteger);
            var numerator = isStart
                ? Number(inputValObj.startNumerator)
                : Number(inputValObj.changeNumerator);
            var denominator = numValue;
            // Convert to improper fraction (no auto-normalization)
            var improperNumerator = integer * denominator + numerator;
            var reqDataNumKey = isStart ? 'startNumerator' : 'countNumerator';
            var reqDataKey = isStart ? 'startDenominator' : 'countDenominator';
            var _dta_6 = createReqData((_c = {},
                _c[reqDataNumKey] = improperNumerator,
                _c[reqDataKey] = denominator,
                _c));
            setNumberChartData(function (prev) { return (__assign(__assign({}, prev), _dta_6)); });
        }
    }, [createReqData, inputValObj]);
    // ==================== MAIN INPUT ROUTER ====================
    var onInputValue = useCallback(function (id, value, _event) {
        var numValue = Number(value || 0);
        var numberType = numberChartData.numberType;
        // Route to appropriate handler based on number type and field
        if (id === 'start-value-integer' || id === 'change-value-integer') {
            // Integer or decimal field
            if (numberType === 0) {
                // Type 0: Integers
                handleIntegerInput(id, value, numValue);
            }
            else if (numberType === 3) {
                // Type 3: Decimals
                handleDecimalInput(id, value, numValue);
            }
            else if (numberType === 2) {
                // Type 2: Mixed numbers - handle integer part
                handleMixedNumberInput(id, value, numValue);
            }
        }
        else if (id === 'start-value-numerator' ||
            id === 'start-value-denominator' ||
            id === 'change-value-numerator' ||
            id === 'change-value-denominator') {
            // Fraction fields
            if (numberType === 1) {
                // Type 1: Fractions
                handleFractionInput(id, value, numValue);
            }
            else if (numberType === 2) {
                // Type 2: Mixed numbers - handle fraction part
                handleMixedNumberInput(id, value, numValue);
            }
        }
    }, [
        numberChartData.numberType,
        handleIntegerInput,
        handleDecimalInput,
        handleFractionInput,
        handleMixedNumberInput,
    ]);
    /* ==================== OLD IMPLEMENTATION (COMMENTED OUT) ====================
    const onInputValue = useCallback(
      (id: string, value: string, _event: any) => {
        const numValue = Number(value || 0);
        const { numberType } = numberChartData;
  
        // Track start value input lengths for validation
        let _key = 'start-value-integer';
        if (id === 'start-value-integer') {
          _key = 'startInteger';
        } else if (id === 'start-value-numerator') {
          _key = 'startNumerator';
        } else if (id === 'start-value-denominator') {
          _key = 'startDenominator';
        } else if (id === 'change-value-integer') {
          _key = 'changeInteger';
        } else if (id === 'change-value-numerator') {
          _key = 'changeNumerator';
        } else if (id === 'change-value-denominator') {
          _key = 'changeDenominator';
        }
        setInputValObj(prev => ({ ...prev, [_key]: value }));
  
        // Validation helpers
        const isInteger = Number.isInteger(numValue);
        const isValidDecimal = VALID_DECIMAL_REGEX.test(value);
  
        // Handle integer input fields (start-value-integer, change-value-integer)
        if (id === 'start-value-integer' || id === 'change-value-integer') {
          if (shouldBlockIntegerInput(numberType, isInteger, isValidDecimal)) {
            return;
          }
          const isStart = id === 'start-value-integer';
  
          // Clear error when user starts typing
          if (isStart) setStartIntError(false);
          else setChangeIntError(false);
  
          // For integers (numberType 0): validate range and type
          if (numberType === 0 && (numValue > 100000 || !Number.isInteger(numValue))) {
            setInputValObj(prev => ({
              ...prev,
              [isStart ? 'startInteger' : 'changeInteger']: '',
            }));
            if (isStart) setStartIntError(true);
            else setChangeIntError(true);
            return;
          }
  
          const reqDataKey = isStart ? 'startNumerator' : 'countNumerator';
          const reqDataDenomKey = isStart ? 'startDenominator' : 'countDenominator';
  
          // For mixed numbers (numberType === 2), combine integer + numerator/denominator into improper fraction
          if (numberType === 2) {
            const integer = numValue;
            const numerator = isStart
              ? Number(inputValObj.startNumerator)
              : Number(inputValObj.changeNumerator);
            const denominator = isStart
              ? Number(inputValObj.startDenominator)
              : Number(inputValObj.changeDenominator);
  
            // Convert mixed number to improper fraction: integer + numerator/denominator = (integer * denominator + numerator) / denominator
            const improperNumerator = integer * denominator + numerator;
            const _dta = createReqData({
              [reqDataKey]: improperNumerator,
              [reqDataDenomKey]: denominator,
            });
            setNumberChartData(prev => ({ ...prev, ..._dta }));
          } else {
            const _dta = createReqData({
              [reqDataKey]: numValue,
              [reqDataDenomKey]: 1,
            });
            setNumberChartData(prev => ({ ...prev, ..._dta }));
          }
        }
        // Handle fraction input fields (numerator/denominator)
        else if (id === 'start-value-numerator' || id === 'change-value-numerator') {
          if (shouldBlockFractionInput(numberType, isInteger)) {
            return;
          }
          const isStart = id === 'start-value-numerator';
          const reqDataKey = isStart ? 'startNumerator' : 'countNumerator';
  
          // For mixed numbers (numberType === 2), combine integer + numerator/denominator into improper fraction
          if (numberType === 2) {
            let integer = isStart
              ? Number(inputValObj.startInteger)
              : Number(inputValObj.changeInteger);
            let numerator = numValue;
            const denominator = isStart
              ? Number(inputValObj.startDenominator)
              : Number(inputValObj.changeDenominator);
  
            // Normalize: if fractional part is improper (numerator >= denominator), extract whole part
            if (numerator >= denominator && denominator > 0) {
              const wholePart = Math.floor(numerator / denominator);
              integer = integer + wholePart;
              numerator = numerator % denominator;
  
              // Update inputValObj with normalized values
              setInputValObj(prev => ({
                ...prev,
                [isStart ? 'startInteger' : 'changeInteger']: String(integer),
                [isStart ? 'startNumerator' : 'changeNumerator']: numerator,
              }));
            }
  
            // Convert mixed number to improper fraction: integer + numerator/denominator = (integer * denominator + numerator) / denominator
            const improperNumerator = integer * denominator + numerator;
            const reqDataDenomKey = isStart ? 'startDenominator' : 'countDenominator';
            const _dta = createReqData({
              [reqDataKey]: improperNumerator,
              [reqDataDenomKey]: denominator,
            });
            setNumberChartData(prev => ({ ...prev, ..._dta }));
          } else {
            const _dta = createReqData({ [reqDataKey]: numValue });
            setNumberChartData(prev => ({ ...prev, ..._dta }));
          }
        } else if (id === 'start-value-denominator' || id === 'change-value-denominator') {
          if (shouldBlockFractionInput(numberType, isInteger)) {
            return;
          }
          const isStart = id === 'start-value-denominator';
          const reqDataKey = isStart ? 'startDenominator' : 'countDenominator';
  
          // For mixed numbers (numberType === 2), combine integer + numerator/denominator into improper fraction
          if (numberType === 2) {
            let integer = isStart
              ? Number(inputValObj.startInteger)
              : Number(inputValObj.changeInteger);
            let numerator = isStart
              ? Number(inputValObj.startNumerator)
              : Number(inputValObj.changeNumerator);
            const denominator = numValue || 1; // Prevent division by zero
  
            // Normalize: if fractional part is improper (numerator >= denominator), extract whole part
            if (numerator >= denominator && denominator > 0) {
              const wholePart = Math.floor(numerator / denominator);
              integer = integer + wholePart;
              numerator = numerator % denominator;
  
              // Update inputValObj with normalized values
              setInputValObj(prev => ({
                ...prev,
                [isStart ? 'startInteger' : 'changeInteger']: String(integer),
                [isStart ? 'startNumerator' : 'changeNumerator']: numerator,
              }));
            }
  
            // Convert mixed number to improper fraction: integer + numerator/denominator = (integer * denominator + numerator) / denominator
            const improperNumerator = integer * denominator + numerator;
            const reqDataNumKey = isStart ? 'startNumerator' : 'countNumerator';
            const _dta = createReqData({
              [reqDataNumKey]: improperNumerator,
              [reqDataKey]: denominator,
            });
            setNumberChartData(prev => ({ ...prev, ..._dta }));
          } else {
            const _dta = createReqData({ [reqDataKey]: numValue || 1 }); // Prevent division by zero
            setNumberChartData(prev => ({ ...prev, ..._dta }));
          }
        }
      },
      [numberChartData.numberType, createReqData],
    );
    ==================== END OLD IMPLEMENTATION ==================== */
    // Handle blur event to round values based on numberType
    var onInputBlur = useCallback(function (id, eventType, value) {
        var _a, _b;
        if (eventType !== 'blur')
            return;
        var numberType = numberChartData.numberType;
        // Skip blur processing for integer type (numberType 0) - validation handled in onInput
        if (numberType === 0) {
            return;
        }
        // Skip blur processing for fraction type (numberType 1) - validation handled in onInput
        if (numberType === 1) {
            return;
        }
        // Skip blur processing for mixed number type (numberType 2) - validation handled in onInput
        if (numberType === 2) {
            return;
        }
        // Skip blur processing for decimal type (numberType 3) - validation handled in onInput
        if (numberType === 3) {
            return;
        }
        // Only decimals (type 3) continue to blur processing
        var numValue = Number(value || 0);
        if (id === 'start-value-integer' || id === 'change-value-integer') {
            var isStart = id === 'start-value-integer';
            var roundedValue = void 0;
            var roundedStr_1;
            if (numberType === 3) {
                // Decimals: round to 2 decimal places
                roundedValue = Math.round(numValue * 100) / 100;
                roundedStr_1 = roundedValue.toString();
            }
            else {
                // Integers (numberType 0, 2): round to whole number
                roundedValue = Math.round(numValue);
                roundedStr_1 = roundedValue.toString();
            }
            // Update inputValObj with rounded value
            if (isStart) {
                setInputValObj(function (prev) { return (__assign(__assign({}, prev), { startInteger: roundedStr_1 })); });
            }
            else {
                setInputValObj(function (prev) { return (__assign(__assign({}, prev), { changeInteger: roundedStr_1 })); });
            }
            // Clear error after successful blur processing
            if (isStart)
                setStartIntError(false);
            else
                setChangeIntError(false);
            // Update chart data with rounded value
            var reqDataKey = id === 'start-value-integer' ? 'startNumerator' : 'countNumerator';
            var reqDataDenomKey = id === 'start-value-integer' ? 'startDenominator' : 'countDenominator';
            if (numberType === 2) {
                // For mixed numbers, combine rounded integer with existing fraction
                var isStart_1 = id === 'start-value-integer';
                var numerator = isStart_1
                    ? Number(inputValObj.startNumerator)
                    : Number(inputValObj.changeNumerator);
                var denominator = isStart_1
                    ? Number(inputValObj.startDenominator)
                    : Number(inputValObj.changeDenominator);
                var improperNumerator = roundedValue * denominator + numerator;
                var _dta_7 = createReqData((_a = {},
                    _a[reqDataKey] = improperNumerator,
                    _a[reqDataDenomKey] = denominator,
                    _a));
                setNumberChartData(function (prev) { return (__assign(__assign({}, prev), _dta_7)); });
            }
            else {
                var _dta_8 = createReqData((_b = {},
                    _b[reqDataKey] = roundedValue,
                    _b[reqDataDenomKey] = 1,
                    _b));
                setNumberChartData(function (prev) { return (__assign(__assign({}, prev), _dta_8)); });
            }
        }
    }, [numberChartData.numberType, createReqData, inputValObj]);
    var UpdatePos = useCallback(function () {
        var container = chartDisplayRef.current;
        var popup = document.querySelector("#numberChartTable".concat(numberChartData.id, "popup"));
        if (!container || !popup)
            return;
        popup.style.transform = 'scale(1)';
        popup.style.transformOrigin = 'center';
        var containerWidth = container.offsetWidth;
        var containerHeight = container.offsetHeight;
        var popupWidth = popup.offsetWidth;
        var popupHeight = popup.offsetHeight;
        if (!popupWidth || !popupHeight)
            return;
        var widthScale = containerWidth / popupWidth;
        var heightScale = containerHeight / popupHeight;
        var scale = widthScale < heightScale ? widthScale : heightScale;
        var scaleVal = scale > 1.5 ? 1.5 : scale;
        var finalScale = (numberChartData.chartType || 0) === 4 ? scaleVal : scale;
        // popup.style.transform = `scale(${scale})`;
        popup.style.transform = "scale(".concat(finalScale, ")");
        setPreviewScale(finalScale);
    }, [numberChartData]);
    var handleResize = useCallback(function () {
        UpdatePos();
    }, [UpdatePos, numberChartData.numberType]);
    useOnResize(handleResize);
    useEffect(function () {
        UpdatePos();
    }, [UpdatePos, numberChartData.numberType]);
    // Memoized handlers for RadioButton and Dropdown
    var handleRadioChange = useCallback(function (value) { return onRadioChange(value); }, [onRadioChange]);
    var handleDropdownChange = useCallback(function (_dropdownId, data) {
        var currentType = numberChartData.numberType;
        var newType = data.index;
        // Clear all errors when switching number type
        setStartIntError(false);
        setChangeIntError(false);
        setStartNumError(false);
        setStartDenError(false);
        setChangeNumError(false);
        setChangeDenError(false);
        setStartDenZeroError(false);
        setChangeDenZeroError(false);
        setStartDecimalPrecisionError(false);
        setChangeDecimalPrecisionError(false);
        // Extract current values
        var currentStartInt = Number(inputValObj.startInteger || 0);
        var currentStartNum = inputValObj.startNumerator;
        var currentStartDenom = inputValObj.startDenominator;
        var currentChangeInt = Number(inputValObj.changeInteger || 0);
        var currentChangeNum = inputValObj.changeNumerator;
        var currentChangeDenom = inputValObj.changeDenominator;
        // Convert start value to decimal (common intermediate format)
        var startDecimal;
        if (currentType === 0) {
            // Integer to decimal
            startDecimal = currentStartInt;
        }
        else if (currentType === 1) {
            // Fraction to decimal
            startDecimal = fractionToDecimal(currentStartNum, currentStartDenom);
        }
        else if (currentType === 2) {
            // Mixed number to decimal
            startDecimal = currentStartInt + fractionToDecimal(currentStartNum, currentStartDenom);
        }
        else {
            // Already decimal
            startDecimal = currentStartInt;
        }
        // Convert change value to decimal
        var changeDecimal;
        if (currentType === 0) {
            // Integer to decimal
            changeDecimal = currentChangeInt;
        }
        else if (currentType === 1) {
            // Fraction to decimal
            changeDecimal = fractionToDecimal(currentChangeNum, currentChangeDenom);
        }
        else if (currentType === 2) {
            // Mixed number to decimal
            changeDecimal = currentChangeInt + fractionToDecimal(currentChangeNum, currentChangeDenom);
        }
        else {
            // Already decimal
            changeDecimal = currentChangeInt;
        }
        // Ensure minimum change value
        if (changeDecimal === 0) {
            changeDecimal = newType === 3 ? 0.01 : 1;
        }
        // Convert from decimal to new type
        var newStartInt, newStartNum, newStartDenom;
        var newChangeInt, newChangeNum, newChangeDenom;
        if (newType === 0) {
            // Convert to integer
            var startRounded = Math.round(startDecimal);
            var changeRounded = Math.max(1, Math.round(changeDecimal));
            newStartInt = String(startRounded);
            newStartNum = startRounded;
            newStartDenom = 1;
            newChangeInt = String(changeRounded);
            newChangeNum = changeRounded;
            newChangeDenom = 1;
        }
        else if (newType === 1) {
            // Convert to fraction
            var startFrac = decimalToFraction(startDecimal);
            newStartInt = '0';
            newStartNum = startFrac.numerator;
            newStartDenom = startFrac.denominator;
            var changeFrac = decimalToFraction(changeDecimal);
            newChangeInt = '0';
            newChangeNum = changeFrac.numerator;
            newChangeDenom = changeFrac.denominator;
        }
        else if (newType === 2) {
            // Convert to mixed number
            var startFrac = decimalToFraction(startDecimal);
            var startMixed = fractionToMixed(startFrac.numerator, startFrac.denominator);
            newStartInt = String(startMixed.integer);
            newStartNum = startMixed.numerator;
            newStartDenom = startMixed.denominator;
            var changeFrac = decimalToFraction(changeDecimal);
            var changeMixed = fractionToMixed(changeFrac.numerator, changeFrac.denominator);
            newChangeInt = String(changeMixed.integer);
            newChangeNum = changeMixed.numerator;
            newChangeDenom = changeMixed.denominator;
        }
        else {
            // Convert to decimal (type 3)
            // For decimals, the value is stored directly as numerator with denominator=1
            newStartInt = startDecimal.toFixed(2);
            newStartNum = startDecimal; // Store decimal directly, not as fraction
            newStartDenom = 1;
            newChangeInt = changeDecimal.toFixed(2);
            newChangeNum = changeDecimal; // Store decimal directly, not as fraction
            newChangeDenom = 1;
        }
        // Calculate improper fractions for API
        var apiStartNum, apiStartDenom;
        var apiChangeNum, apiChangeDenom;
        if (newType === 2) {
            // For mixed numbers, convert to improper fractions
            var startImproper = mixedToFraction(Number(newStartInt), newStartNum, newStartDenom);
            apiStartNum = startImproper.numerator;
            apiStartDenom = startImproper.denominator;
            var changeImproper = mixedToFraction(Number(newChangeInt), newChangeNum, newChangeDenom);
            apiChangeNum = changeImproper.numerator;
            apiChangeDenom = changeImproper.denominator;
        }
        else {
            apiStartNum = newStartNum;
            apiStartDenom = newStartDenom;
            apiChangeNum = newChangeNum;
            apiChangeDenom = newChangeDenom;
        }
        // Update input values
        setInputValObj(function (prev) { return (__assign(__assign({}, prev), { startInteger: newStartInt, startNumerator: newStartNum, startDenominator: newStartDenom, changeInteger: newChangeInt, changeNumerator: newChangeNum, changeDenominator: newChangeDenom })); });
        // Update chart data for preview
        var _dta = createReqData({
            numberType: newType,
            startNumerator: apiStartNum,
            startDenominator: apiStartDenom,
            countNumerator: apiChangeNum,
            countDenominator: apiChangeDenom,
            countDirection: 0,
        });
        setNumberChartData(function (prev) { return (__assign(__assign({}, prev), _dta)); });
        setPopupAnnounceText('');
        requestAnimationFrame(function () {
            var _a;
            setPopupAnnounceText(t('text.numberTypeChangedAnnounce', { type: (_a = NUMBER_TYPE_OPTS[newType]) !== null && _a !== void 0 ? _a : '' }));
        });
    }, [createReqData, numberChartData.numberType, inputValObj, NUMBER_TYPE_OPTS]);
    var handleSimplifyFractionsClick = useCallback(function () {
        var _a;
        var newSimplifyValue = !((_a = numberChartData === null || numberChartData === void 0 ? void 0 : numberChartData.simplfyFractions) !== null && _a !== void 0 ? _a : false);
        setNumberChartData(function (prev) { return (__assign(__assign({}, prev), { simplfyFractions: newSimplifyValue })); });
        setPopupAnnounceText('');
        requestAnimationFrame(function () {
            setPopupAnnounceText(newSimplifyValue ? t('text.simplifyFractionsOn') : t('text.simplifyFractionsOff'));
        });
    }, [numberChartData === null || numberChartData === void 0 ? void 0 : numberChartData.simplfyFractions]);
    var handleCountDirectionChange = useCallback(function (idx) {
        var _dta = createReqData({ countDirection: idx });
        setNumberChartData(function (prev) { return (__assign(__assign({}, prev), _dta)); });
    }, [createReqData]);
    var handleDirectionChange = useCallback(function (value) {
        setNumberChartData(function (prev) { return (__assign(__assign({}, prev), { direction: value })); });
        var dirKeys = [
            'text.dirTopRight',
            'text.dirLeftDown',
            'text.dirBottomRight',
            'text.dirBottomUp',
        ];
        setPopupAnnounceText('');
        requestAnimationFrame(function () {
            var _a;
            setPopupAnnounceText(t('text.directionChangedAnnounce', { direction: t((_a = dirKeys[value]) !== null && _a !== void 0 ? _a : 'text.dirTopRight') }));
        });
    }, []);
    var handleActionBtnClick = useCallback(function (type) {
        onBtnClick(type);
    }, [onBtnClick]);
    // Memoize MathEditor so it only re-renders when numberType changes
    var displayValuesMathEditor = useMemo(function () { return (_jsx(MathEditor, { data: {
            type: 'data',
            data: {
                text: convertToMathEditorFormat(DISPLAY_VALUES[numberChartData.numberType]),
                color: '#293273',
                alignment: 'center',
            },
        }, className: "displayValuesMathEditor", isTextUpdate: false, idPreffix: "numberChartPopup-displayValues", index: 0, disableTransform: true, isManipulative: true, viewonly: true })); }, [numberChartData.numberType]);
    return (_jsx("div", { className: "numberChartsWrapper", "aria-hidden": false, onKeyDown: function (e) {
            if (['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown'].includes(e.key)) {
                e.stopPropagation();
            }
        }, children: _jsxs("div", { className: "holder", children: [_jsx(CustomScrollbar, { direction: "vertical", children: _jsxs("div", { className: "scrollbarContent", children: [_jsxs("fieldset", { className: "radioFieldset", children: [_jsx("legend", { className: "sronly", children: t('text.gridSize') }), _jsx("div", { className: "gridText", "aria-hidden": true, children: t('text.gridSize') }), _jsx(RadioButton, { id: "numCharts-type", options: RADIO_OPTS, selected: numberChartData.chartType, onChange: handleRadioChange, className: "radioGap" })] }), _jsxs("div", { className: "activityContainer ".concat((numberChartData === null || numberChartData === void 0 ? void 0 : numberChartData.chartType) === 4 ? 'customContainer' : ''), children: [_jsx("div", { className: "".concat((numberChartData === null || numberChartData === void 0 ? void 0 : numberChartData.chartType) === 4 ? 'numberChartsHolder' : '', " numberChartsHolder").concat(numberChartData === null || numberChartData === void 0 ? void 0 : numberChartData.chartType), children: _jsx("div", { className: "holderDiv chartType_".concat(numberChartData === null || numberChartData === void 0 ? void 0 : numberChartData.chartType), ref: chartDisplayRef, children: _jsx(NumberCharts, { data: numberChartData, setData: setNumberChartData, id: numberChartData.id || '', calledFrom: "popup", displayScale: previewScale }) }) }), (numberChartData === null || numberChartData === void 0 ? void 0 : numberChartData.chartType) === 4 && (_jsxs("div", { className: "customWrapper", children: [_jsxs("div", { className: "upperContainer", children: [_jsx("div", { className: "header", id: "numberTypeLabel", children: t('text.numberType') }), _jsxs("div", { className: "ddHolder", children: [_jsx(Dropdown, { id: "number-type-selection-dropDown", className: "dropdownField", options: NUMBER_TYPE_OPTS, value: NUMBER_TYPE_OPTS[numberChartData.numberType], onChange: handleDropdownChange, labelId: 'numberTypeLabel', align: "left", optionWidthType: "tillOption", ddHeight: 170, controlHeightFromOutside: true, selectedDisable: true }), _jsx("div", { className: "displayValues", inert: true, "aria-hidden": "true", children: displayValuesMathEditor }), (numberChartData.numberType === 1 || numberChartData.numberType === 2) && (_jsxs(_Fragment, { children: [_jsx(DividerLine, { className: "vertical" }), _jsx(CheckBox, { label: t('text.simplfyFrac'), checked: (_a = numberChartData === null || numberChartData === void 0 ? void 0 : numberChartData.simplfyFractions) !== null && _a !== void 0 ? _a : false, onClick: handleSimplifyFractionsClick, ariaLabel: t('text.simplfyFrac') })] }))] })] }), _jsx(DividerLine, { className: "opacity15Percent" }), _jsxs("div", { className: "middleContainer", children: [_jsxs("div", { className: "sizeWrapper", children: [_jsxs("div", { style: {
                                                                    display: 'flex',
                                                                    flexDirection: 'column',
                                                                    alignItems: 'flex-start',
                                                                }, children: [_jsxs("div", { className: "wrapper", children: [_jsx("div", { className: "wrapperTitle", id: "row-selector-label", "aria-hidden": "true", children: t('text.row1to20') }), _jsx(InputBox, { id: "row-selector", type: "number", step: 1, min: 1, max: 20, isNumeric: false, value: inputValObj.row < 1 ? '' : inputValObj.row.toString(), onInput: onUpdateRowCol, defaultCSS: rowError ? 'inputError' : '', ariaLabel: t('text.rowsRangeLabel'), ariaInvalid: rowError, invalidKeys: ['ArrowUp', 'ArrowDown'] })] }), _jsxs("div", { className: "inputErrorText inline ".concat(rowError ? 'visible' : ''), "aria-hidden": !rowError, role: "alert", children: [_jsx("span", { "aria-hidden": "true", children: "* " }), t('text.numberChartErrorMaxRows')] })] }), _jsxs("div", { style: {
                                                                    display: 'flex',
                                                                    flexDirection: 'column',
                                                                    alignItems: 'flex-start',
                                                                }, children: [_jsxs("div", { className: "wrapper", children: [_jsx("div", { className: "wrapperTitle", id: "col-selector-label", "aria-hidden": "true", children: t('text.col1to20') }), _jsx(InputBox, { id: "col-selector", type: "number", step: 1, min: 1, max: 20, isNumeric: false, value: inputValObj.col < 1 ? '' : inputValObj.col.toString(), onInput: onUpdateRowCol, defaultCSS: colError ? 'inputError' : '', ariaLabel: t('text.columnsRangeLabel'), ariaInvalid: colError, invalidKeys: ['ArrowUp', 'ArrowDown'] })] }), _jsxs("div", { className: "inputErrorText inline ".concat(colError ? 'visible' : ''), "aria-hidden": !colError, role: "alert", children: [_jsx("span", { "aria-hidden": "true", children: "* " }), t('text.numberChartErrorMaxRows')] })] })] }), _jsxs("div", { className: "valueWrapper", children: [_jsxs("div", { style: {
                                                                    display: 'flex',
                                                                    flexDirection: 'column',
                                                                    alignItems: 'flex-start',
                                                                    width: '100%',
                                                                }, children: [_jsxs("div", { className: "commonWrapper startVal", children: [_jsx("div", { id: "start-value-label", "aria-hidden": "true", children: t('text.startVal') }), _jsxs("div", { style: flexCenterStyle, children: [(numberChartData.numberType === 0 ||
                                                                                        numberChartData.numberType === 2 ||
                                                                                        numberChartData.numberType === 3) && (_jsx("div", { children: _jsx(InputBox, { id: "start-value-integer", onInput: onInputValue, onChange: onInputBlur, min: 0, max: 100000, type: "number", step: numberChartData.numberType === 3 ? 0.01 : 1, value: inputValObj.startInteger, defaultCSS: startIntError || startDecimalPrecisionError ? 'inputError' : '', ariaLabelledBy: "start-value-label", ariaInvalid: startIntError || startDecimalPrecisionError }) })), (numberChartData.numberType === 1 ||
                                                                                        numberChartData.numberType === 2) && (_jsxs("div", { children: [_jsx(InputBox, { id: "start-value-numerator", onInput: onInputValue, min: 1, max: 100000, type: "number", value: inputValObj.startNumerator.toString(), defaultCSS: startNumError ? 'inputError' : '', ariaLabel: t('text.startNumerator'), ariaInvalid: startNumError }), _jsx(DividerLine, { className: "margin8Px" }), _jsx(InputBox, { id: "start-value-denominator", onInput: onInputValue, type: "number", min: 1, value: inputValObj.startDenominator.toString(), defaultCSS: startDenError || startDenZeroError ? 'inputError' : '', ariaLabel: t('text.startDenominator'), ariaInvalid: startDenError || startDenZeroError })] }))] })] }), _jsxs("div", { className: "inputErrorText inline ".concat(startIntError ||
                                                                            startNumError ||
                                                                            startDenError ||
                                                                            startDenZeroError ||
                                                                            startDecimalPrecisionError
                                                                            ? 'visible'
                                                                            : ''), children: [_jsxs("div", { className: "errorSpacer", "aria-hidden": "true", children: [_jsx("span", { "aria-hidden": "true", children: "* " }), t('text.numberChartErrorMaxFraction')] }), _jsxs("div", { className: "errorMessage".concat(startDecimalPrecisionError ? ' active' : ''), role: startDecimalPrecisionError ? 'alert' : undefined, "aria-hidden": !startDecimalPrecisionError, children: [_jsx("span", { "aria-hidden": "true", children: "* " }), t('text.numberChartErrorDecimalPrecision')] }), _jsxs("div", { className: "errorMessage".concat(startIntError && !startDecimalPrecisionError ? ' active' : ''), role: startIntError && !startDecimalPrecisionError ? 'alert' : undefined, "aria-hidden": !(startIntError && !startDecimalPrecisionError), children: [_jsx("span", { "aria-hidden": "true", children: "* " }), t('text.numberChartErrorMaxInteger')] }), _jsxs("div", { className: "errorMessage".concat(startDenZeroError && !startDecimalPrecisionError && !startIntError
                                                                                    ? ' active'
                                                                                    : ''), role: startDenZeroError && !startDecimalPrecisionError && !startIntError
                                                                                    ? 'alert'
                                                                                    : undefined, "aria-hidden": !(startDenZeroError && !startDecimalPrecisionError && !startIntError), children: [_jsx("span", { "aria-hidden": "true", children: "* " }), t('text.numberChartErrorDenominatorZero')] }), _jsxs("div", { className: "errorMessage".concat((startNumError || startDenError) &&
                                                                                    !startDecimalPrecisionError &&
                                                                                    !startIntError &&
                                                                                    !startDenZeroError
                                                                                    ? ' active'
                                                                                    : ''), role: (startNumError || startDenError) &&
                                                                                    !startDecimalPrecisionError &&
                                                                                    !startIntError &&
                                                                                    !startDenZeroError
                                                                                    ? 'alert'
                                                                                    : undefined, "aria-hidden": !((startNumError || startDenError) &&
                                                                                    !startDecimalPrecisionError &&
                                                                                    !startIntError &&
                                                                                    !startDenZeroError), children: [_jsx("span", { "aria-hidden": "true", children: "* " }), t('text.numberChartErrorMaxFraction')] })] })] }), _jsx(RadioButton, { options: ADJUSTMENT_OPTS, id: "increase-decrease-selection-type", selected: numberChartData.countDirection, className: "radioINCDECcss", onChange: handleCountDirectionChange }), _jsxs("div", { style: {
                                                                    display: 'flex',
                                                                    flexDirection: 'column',
                                                                    alignItems: 'flex-start',
                                                                    width: '100%',
                                                                }, children: [_jsxs("div", { className: "commonWrapper changeVal", children: [_jsx("div", { id: "change-value-label", "aria-hidden": "true", children: t('text.changeVal') }), _jsxs("div", { style: flexCenterStyle, children: [(numberChartData.numberType === 0 ||
                                                                                        numberChartData.numberType === 2 ||
                                                                                        numberChartData.numberType === 3) && (_jsx("div", { children: _jsx(InputBox, { id: "change-value-integer", onInput: onInputValue, onChange: onInputBlur, min: numberChartData.numberType === 3 ? 0.01 : 1, max: 100000, defaultCSS: changeIntError || changeDecimalPrecisionError
                                                                                                ? 'inputError'
                                                                                                : '', type: "number", step: numberChartData.numberType === 3 ? 0.01 : 1, value: inputValObj.changeInteger, ariaLabelledBy: "change-value-label", ariaInvalid: changeIntError || changeDecimalPrecisionError }) })), (numberChartData.numberType === 1 ||
                                                                                        numberChartData.numberType === 2) && (_jsxs("div", { children: [_jsx(InputBox, { id: "change-value-numerator", onInput: onInputValue, type: "number", min: 1, max: 100000, value: inputValObj.changeNumerator.toString(), defaultCSS: changeNumError ? 'inputError' : '', ariaLabel: t('text.changeNumerator'), ariaInvalid: changeNumError }), _jsx(DividerLine, { className: "margin8Px" }), _jsx(InputBox, { id: "change-value-denominator", onInput: onInputValue, type: "number", min: 1, value: inputValObj.changeDenominator.toString(), defaultCSS: changeDenError || changeDenZeroError ? 'inputError' : '', ariaLabel: t('text.changeDenominator'), ariaInvalid: changeDenError || changeDenZeroError })] }))] })] }), _jsxs("div", { className: "inputErrorText inline ".concat(changeIntError ||
                                                                            changeNumError ||
                                                                            changeDenError ||
                                                                            changeDenZeroError ||
                                                                            changeDecimalPrecisionError
                                                                            ? 'visible'
                                                                            : ''), children: [_jsxs("div", { className: "errorSpacer", "aria-hidden": "true", children: [_jsx("span", { "aria-hidden": "true", children: "* " }), t('text.numberChartErrorMaxFraction')] }), _jsxs("div", { className: "errorMessage".concat(changeDecimalPrecisionError ? ' active' : ''), role: changeDecimalPrecisionError ? 'alert' : undefined, "aria-hidden": !changeDecimalPrecisionError, children: [_jsx("span", { "aria-hidden": "true", children: "* " }), t('text.numberChartErrorDecimalPrecision')] }), _jsxs("div", { className: "errorMessage".concat(changeIntError && !changeDecimalPrecisionError ? ' active' : ''), role: changeIntError && !changeDecimalPrecisionError ? 'alert' : undefined, "aria-hidden": !(changeIntError && !changeDecimalPrecisionError), children: [_jsx("span", { "aria-hidden": "true", children: "* " }), t('text.numberChartErrorMaxInteger')] }), _jsxs("div", { className: "errorMessage".concat(changeDenZeroError && !changeDecimalPrecisionError && !changeIntError
                                                                                    ? ' active'
                                                                                    : ''), role: changeDenZeroError && !changeDecimalPrecisionError && !changeIntError
                                                                                    ? 'alert'
                                                                                    : undefined, "aria-hidden": !(changeDenZeroError &&
                                                                                    !changeDecimalPrecisionError &&
                                                                                    !changeIntError), children: [_jsx("span", { "aria-hidden": "true", children: "* " }), t('text.numberChartErrorDenominatorZero')] }), _jsxs("div", { className: "errorMessage".concat((changeNumError || changeDenError) &&
                                                                                    !changeDecimalPrecisionError &&
                                                                                    !changeIntError &&
                                                                                    !changeDenZeroError
                                                                                    ? ' active'
                                                                                    : ''), role: (changeNumError || changeDenError) &&
                                                                                    !changeDecimalPrecisionError &&
                                                                                    !changeIntError &&
                                                                                    !changeDenZeroError
                                                                                    ? 'alert'
                                                                                    : undefined, "aria-hidden": !((changeNumError || changeDenError) &&
                                                                                    !changeDecimalPrecisionError &&
                                                                                    !changeIntError &&
                                                                                    !changeDenZeroError), children: [_jsx("span", { "aria-hidden": "true", children: "* " }), t('text.numberChartErrorMaxFraction')] })] })] })] })] }), _jsx(DividerLine, { className: "opacity15Percent" }), _jsxs("div", { className: "upperContainer bottomContainer ", children: [_jsx("div", { className: "header", "aria-hidden": "true", children: t('text.direction') }), _jsxs("fieldset", { className: "radioFieldsetDirection", children: [_jsx("legend", { className: "sronly", children: t('text.direction') }), _jsx(RadioButton, { id: "direction", options: RADIO_DIRECTION_OPTS, selected: numberChartData.direction, onChange: handleDirectionChange, className: "radioGap", optionsType: "image", useAriaLabelFromOutside: true, ariaLabels: [
                                                                    t('text.dirTopRight'),
                                                                    t('text.dirLeftDown'),
                                                                    t('text.dirBottomRight'),
                                                                    t('text.dirBottomUp'),
                                                                ], tooltips: RADIO_DIRECTION_TOOLTIPS })] })] })] }))] })] }) }), _jsx(FooterActionsButtons, { disableDoneBtn: isDoneDisabled, onActionBtnClick: handleActionBtnClick }), _jsx("div", { role: "status", "aria-live": "polite", "aria-atomic": "true", className: "srOnly", children: popupAnnounceText })] }) }));
};
export default NumberChartManipulativePopup;
